import { graffitisApi } from '@/api/graffitis'
import type { GraffitiFromAPI } from '@/types/entities'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

interface CachedGraffitiList {
  data: GraffitiFromAPI[]
  timestamp: number
  expiresAt: number
}

interface CachedGraffitiDetail {
  data: {
    graffiti: GraffitiFromAPI
    images: any[]
    references: any[]
    links: any[]
  }
  timestamp: number
  expiresAt: number
}

const LIST_CACHE_KEY = 'bdc-graffitis-list'
const DETAIL_CACHE_PREFIX = 'bdc-graffiti-detail-'
const LIST_CACHE_TTL = 60 * 60 * 1000 // 1 heure
const DETAIL_CACHE_TTL = 60 * 60 * 1000 // 1 heure

export const useGraffitiStore = defineStore('graffiti', () => {
  // État réactif
  const allGraffitis = ref<GraffitiFromAPI[]>([])
  const graffitiDetails = ref<
    Map<
      number,
      {
        graffiti: GraffitiFromAPI
        images: any[]
        references: any[]
        links: any[]
      }
    >
  >(new Map())
  const isLoading = ref(false)
  const isLoadingDetail = ref<Map<number, boolean>>(new Map())
  const error = ref<string | null>(null)
  const lastFetchTime = ref<number | null>(null)

  // Getters computed
  const graffitiCount = computed(() => allGraffitis.value.length)
  const getGraffitiById = computed(() => (id: number) => {
    return allGraffitis.value.find(g => g.idGraf === id) || null
  })

  /**
   * Charger la liste complète depuis le cache localStorage
   */
  function loadListFromCache(): GraffitiFromAPI[] | null {
    try {
      const cached = localStorage.getItem(LIST_CACHE_KEY)
      if (!cached) return null

      const cachedData: CachedGraffitiList = JSON.parse(cached)
      const now = Date.now()

      if (now < cachedData.expiresAt) {
        //console.log('[BdC-GraffitiStore] Liste chargée depuis le cache')
        return cachedData.data
      }

      localStorage.removeItem(LIST_CACHE_KEY)
      return null
    } catch (err) {
      console.warn('[BdC-GraffitiStore] Erreur lecture cache liste:', err)
      localStorage.removeItem(LIST_CACHE_KEY)
      return null
    }
  }

  /**
   * Sauvegarder la liste dans le cache localStorage
   */
  function saveListToCache(data: GraffitiFromAPI[]): void {
    try {
      const cached: CachedGraffitiList = {
        data,
        timestamp: Date.now(),
        expiresAt: Date.now() + LIST_CACHE_TTL,
      }
      localStorage.setItem(LIST_CACHE_KEY, JSON.stringify(cached))
      // console.log('[BdC-GraffitiStore] Liste sauvegardée dans le cache')
    } catch (err) {
      console.warn('[BdC-GraffitiStore] Erreur sauvegarde cache liste:', err)
    }
  }

  /**
   * Charger un détail depuis le cache localStorage
   */
  function loadDetailFromCache(id: number): {
    graffiti: GraffitiFromAPI
    images: any[]
    references: any[]
    links: any[]
  } | null {
    try {
      const cached = localStorage.getItem(`${DETAIL_CACHE_PREFIX}${id}`)
      if (!cached) return null

      const cachedData: CachedGraffitiDetail = JSON.parse(cached)
      const now = Date.now()

      if (now < cachedData.expiresAt) {
        console.log(`[BdC-GraffitiStore] Détail ${id} chargé depuis le cache`)
        return cachedData.data
      }

      localStorage.removeItem(`${DETAIL_CACHE_PREFIX}${id}`)
      return null
    } catch (err) {
      console.warn(`[BdC-GraffitiStore] Erreur lecture cache détail ${id}:`, err)
      localStorage.removeItem(`${DETAIL_CACHE_PREFIX}${id}`)
      return null
    }
  }

  /**
   * Sauvegarder un détail dans le cache localStorage
   */
  function saveDetailToCache(
    id: number,
    data: {
      graffiti: GraffitiFromAPI
      images: any[]
      references: any[]
      links: any[]
    }
  ): void {
    try {
      const cached: CachedGraffitiDetail = {
        data,
        timestamp: Date.now(),
        expiresAt: Date.now() + DETAIL_CACHE_TTL,
      }
      localStorage.setItem(`${DETAIL_CACHE_PREFIX}${id}`, JSON.stringify(cached))
      //console.log(`[BdC-GraffitiStore] Détail ${id} sauvegardé dans le cache`)
    } catch (err) {
      console.warn(`[BdC-GraffitiStore] Erreur sauvegarde cache détail ${id}:`, err)
    }
  }

  /**
   * Charger tous les graffitis (liste complète)
   * @param forceRefresh Force le rechargement depuis l'API même si le cache est valide
   */
  async function loadAllGraffitis(forceRefresh = false): Promise<void> {
    // Vérifier le cache d'abord si pas de force refresh
    if (!forceRefresh) {
      const cached = loadListFromCache()
      if (cached && cached.length > 0) {
        allGraffitis.value = cached
        lastFetchTime.value = Date.now()
        //console.log(`[BdC-GraffitiStore] ${cached.length} graffitis chargés depuis le cache`)
        return
      }
    }

    isLoading.value = true
    error.value = null
    try {
      //console.log("[BdC-GraffitiStore] Chargement de tous les graffitis depuis l'API")
      const data = await graffitisApi.getAll()
      allGraffitis.value = data
      lastFetchTime.value = Date.now()
      saveListToCache(data)
      // console.log(`[BdC-GraffitiStore] ${data.length} graffitis chargés depuis l'API`)
    } catch (err) {
      console.error('[BdC-GraffitiStore] Erreur chargement liste graffitis:', err)
      error.value = err instanceof Error ? err.message : 'Erreur lors du chargement des graffitis'
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Charger un graffiti complet avec toutes ses relations
   * @param id ID du graffiti (idGraf)
   * @param forceRefresh Force le rechargement depuis l'API même si le cache est valide
   */
  async function loadGraffitiDetail(
    id: number,
    forceRefresh = false
  ): Promise<{
    graffiti: GraffitiFromAPI
    images: any[]
    references: any[]
    links: any[]
  } | null> {
    // Vérifier le cache d'abord si pas de force refresh
    if (!forceRefresh) {
      const cached = loadDetailFromCache(id)
      if (cached) {
        graffitiDetails.value.set(id, cached)
        return cached
      }
    }

    isLoadingDetail.value.set(id, true)
    try {
      //console.log(`[BdC-GraffitiStore] Chargement détail graffiti ${id} depuis l'API`)
      const data = await graffitisApi.getByIdComplete(id)
      graffitiDetails.value.set(id, data)
      saveDetailToCache(id, data)
      console.log(`[BdC-GraffitiStore] Détail ${id} chargé depuis l'API`)
      return data
    } catch (err) {
      console.error(`[BdC-GraffitiStore] Erreur chargement détail ${id}:`, err)
      return null
    } finally {
      isLoadingDetail.value.set(id, false)
    }
  }

  /**
   * Invalider le cache de la liste
   */
  function invalidateListCache(): void {
    localStorage.removeItem(LIST_CACHE_KEY)
    lastFetchTime.value = null
    console.log('[BdC-GraffitiStore] Cache liste invalidé')
  }

  /**
   * Invalider le cache d'un détail
   */
  function invalidateDetailCache(id: number): void {
    localStorage.removeItem(`${DETAIL_CACHE_PREFIX}${id}`)
    graffitiDetails.value.delete(id)
    console.log(`[BdC-GraffitiStore] Cache détail ${id} invalidé`)
  }

  /**
   * Invalider tous les caches
   */
  function invalidateAllCaches(): void {
    invalidateListCache()
    graffitiDetails.value.clear()
    // Supprimer tous les caches de détails
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(DETAIL_CACHE_PREFIX)) {
        localStorage.removeItem(key)
      }
    }
    console.log('[BdC-GraffitiStore] Tous les caches invalidés')
  }

  return {
    // État
    allGraffitis,
    graffitiDetails,
    isLoading,
    isLoadingDetail,
    error,
    lastFetchTime,

    // Getters
    graffitiCount,
    getGraffitiById,

    // Actions
    loadAllGraffitis,
    loadGraffitiDetail,
    invalidateListCache,
    invalidateDetailCache,
    invalidateAllCaches,
  }
})
